/**
 * uuidv7-generator.ts
 *
 * RFC 9562 compliant, monotonically sortable UUIDv7 implementation.
 * Structure: 48-bit unix timestamp (ms) | 4-bit version (0b0111) | 12-bit sequence | 2-bit variant (0b10) | 62-bit entropy
 * Browser & Node.js isomorphic (uses globalThis.crypto).
 */

function getSecureRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
    return bytes;
  }
  // Fallback for non-crypto environments
  for (let i = 0; i < length; i++) {
    bytes[i] = Math.floor(Math.random() * 256);
  }
  return bytes;
}

export class UuidV7 {
  private static lastTimestamp = -1;
  private static sequenceCounter = 0;

  /**
   * Generates an RFC 9562 compliant, monotonically sortable UUIDv7 string.
   */
  public static generate(): string {
    let now = Date.now();

    if (now === this.lastTimestamp) {
      this.sequenceCounter++;
      if (this.sequenceCounter > 0xfff) {
        // Sequence overflow: spin to next millisecond
        while (Date.now() <= now) {
          /* spin */
        }
        now = Date.now();
        this.lastTimestamp = now;
        this.sequenceCounter = 0;
      }
    } else {
      this.lastTimestamp = now;
      this.sequenceCounter = 0;
    }

    const entropy = getSecureRandomBytes(10);
    const timeMs = now;

    const timeHex = timeMs.toString(16).padStart(12, '0');

    // Bytes 0-5: Timestamp
    const p1 = timeHex.slice(0, 8);
    const p2 = timeHex.slice(8, 12);

    // Byte 6: 4-bit version (7) | top 4-bits sequence
    const verSeq = (0x7000 | (this.sequenceCounter & 0x0fff)).toString(16).padStart(4, '0');

    // Byte 8: 2-bit variant (0b10) | 6-bit entropy
    const varByte = (0x80 | (entropy[0] & 0x3f)).toString(16).padStart(2, '0');
    
    // Remaining 9 bytes of entropy converted to hex
    let restEntropy = '';
    for (let i = 1; i < entropy.length; i++) {
      restEntropy += entropy[i].toString(16).padStart(2, '0');
    }

    return `${p1}-${p2}-${verSeq}-${varByte}${restEntropy.slice(0, 2)}-${restEntropy.slice(2, 14)}`.toLowerCase();
  }

  public static isValid(uuid: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid);
  }
}
